import {
  DvGridCoreDirective,
  DvGridWithSidesheetService,
} from '@devinco/ui/grid';
import { Directive, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { DvSplitterEventsService } from '@devinco/ui/dds/splitter';
import { VtSplitterService } from '@shared/services/grid/vt-splitter.service';
import { AgGridAngular } from 'ag-grid-angular';
import {
  AgGridEvent,
  FirstDataRenderedEvent,
  GridReadyEvent,
  IRowNode,
} from 'ag-grid-community';
import { takeUntil } from 'rxjs';
import { NEW_ENTITY_ROUTE_PARAM } from '@shared/constants';

/** should be used instead of dvGridWithSidesheet. Responsible for deselecting rows when
 * grid is loaded with empty data.
 */
@Directive({
  selector: 'ag-grid-angular[vtGridWithSidesheet]',
  standalone: false,
  host: {
    '(firstDataRendered)': 'onFirstDataRendered($event)',
    '(modelUpdated)': 'onModelUpdated($event)',
    '(selectionChanged)': 'onSelectionChanged($event)',
    '(gridReady)': 'onGridReady($event)',
  },
})
export class VtSplitterDirective extends DvGridCoreDirective {
  private splitterService = inject(VtSplitterService);
  private activatedRoute = inject(ActivatedRoute);
  private gridService = inject(DvGridWithSidesheetService);
  private splitterEvents = inject(DvSplitterEventsService);
  private router = inject(Router);

  constructor() {
    super(inject(AgGridAngular));
    this.gridService.agGrid = this.agGrid;
    this.subscribeToRouterEvents();
  }

  onGridReady(evt: Event): void {
    const event = evt as unknown as GridReadyEvent;
    this.splitterService.agGrid = this.agGrid;
    event.context.gridDataLoaded$
      ?.pipe(takeUntil(this.destroy$))
      .subscribe((rowNumber: number) => {
        if (rowNumber === 0) {
          event.api.clearFocusedCell();
          event.api.deselectAll();
        }
      });
  }

  /** On first data rendered:
   * 1. If entityId in url => Try select row with id, ensure node is visible and focus first cell
   * 2. Else => Select first row and focus first cell */
  onFirstDataRendered(event: Event): void {
    const firstDataRenderedEvent = event as unknown as FirstDataRenderedEvent;
    const { api, firstRow } = firstDataRenderedEvent;
    const entityId = this.getEntityId(this.activatedRoute?.firstChild || null);
    const firstColumn = api.getAllDisplayedColumns()[0];
    if (entityId) {
      this.splitterEvents.isSplitterOpened$?.next(true);
      const node = api.getRowNode(entityId);
      this.focusAndSelectNode(node);
    } else {
      api.getDisplayedRowAtIndex(firstRow)?.setSelected(true, true);
      api.clearCellSelection();
      api.setFocusedCell(firstRow, firstColumn);
    }
  }

  /** On model updated:
   * 1. Try reselect row with entityId from url */
  onModelUpdated(event: Event): void {
    const agGridEvent = event as unknown as AgGridEvent<unknown>;
    const { api } = agGridEvent;
    const entityId = this.getEntityId(this.activatedRoute.firstChild || null);
    const selectedNode = api.getSelectedNodes()[0] as IRowNode | undefined;
    // Will reselect row even after server side refresh
    if (entityId && selectedNode && selectedNode.id !== entityId) {
      api.getRowNode(entityId)?.setSelected(true, true);
    }
  }

  /** On selection changed:
   * 1. If selected node and no entityId in url
   *     - navigate by node.id
   *     - sidesheet open
   * 2. If selected node and entityId in url
   *     - navigate by node.id (if they differ)
   * 3. If no selected node and no entityId in url
   *     - navigate to root (for ActivatedRoute)
   *     - sidesheet close
   */
  onSelectionChanged(event: Event): void {
    const agGridEvent = event as unknown as AgGridEvent;
    const { api } = agGridEvent;

    const node = api.getSelectedNodes()[0] as IRowNode | undefined;

    // grouping in tree grids is supported by ag-grid
    // but tree grid nodes have group array set (from v32)
    if (node && node.group && !api.getGridOption('treeData')) return;

    // transient row (e.g. mid server-side reload) without a resolved id yet;
    // ignore until a selectionChanged fires with the real row data/id
    if (node && node.id === undefined) return;

    const entityId = this.getEntityId(this.activatedRoute.firstChild || null);

    if (!node) {
      if (entityId !== NEW_ENTITY_ROUTE_PARAM) {
        this.router
          .navigate(['./'], {
            relativeTo: this.activatedRoute,
            queryParamsHandling: 'merge',
          })
          .catch(() => {});
        this.splitterEvents.isSplitterOpened$.next(false);
      } else {
        this.splitterEvents.isSplitterOpened$.next(true);
      }
      return;
    }

    if (!entityId) {
      this.router
        .navigate([node.id], {
          relativeTo: this.activatedRoute,
          queryParamsHandling: 'merge',
        })
        .catch(() => {});
      this.splitterEvents.isSplitterOpened$?.next(true);
    } else if (entityId) {
      if (node.id !== entityId) {
        const { parent, routeConfig } = this.activatedRoute.snapshot;
        const prePath = routeConfig?.path || parent?.routeConfig?.path;
        const urlPartOld = `${prePath}/${entityId}`;
        const urlPartNew = `${prePath}/${node.id}`;
        const url = this.router.url.replace(urlPartOld, urlPartNew);

        this.router
          .navigateByUrl(url)
          .then(navigated => {
            if (!navigated) {
              // Navigation was stopped, reselect previous
              const node = this.agGrid.api.getRowNode(entityId);
              node?.setSelected(true, true);
            }
          })
          .catch(() => {
            // Navigation failed, ignore in non-routing environments
          });
      }
    }
  }

  /** Gets entityId from url*/
  private getEntityId(route: ActivatedRoute | null): string | undefined {
    if (route) {
      const { params } = route.snapshot;
      const [, entityId] = Object.entries<string>(params)[0];
      return entityId as string;
    }
    return undefined;
  }

  /** On router navigation changes:
   * 1. Get entityId from url
   * 2. Try find a row node with the entityId and select it */
  private subscribeToRouterEvents(): void {
    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: unknown) => {
        if (event instanceof NavigationEnd) {
          const { firstChild } = this.activatedRoute;
          if (firstChild) {
            const { params } = firstChild.snapshot;
            if (Object.keys(params).length) {
              const [, entityId] = Object.entries<string>(params)[0];
              if (entityId === NEW_ENTITY_ROUTE_PARAM) {
                this.agGrid.api.deselectAll();
              } else {
                const selectedNode = this.agGrid.api.getSelectedNodes()[0] as
                  | IRowNode
                  | undefined;
                if (selectedNode?.id !== entityId) {
                  const node = this.agGrid.api.getRowNode(entityId);
                  this.focusAndSelectNode(node);
                }
              }
            }
          }
        }
      });
  }
}
